import { Arrays } from "../../core/Arrays";
import { StringDictionary, IStringDictionary } from "../../core/StringDictionary";
import { CartItem, ICartItem } from "./CartItem";
import { Customer, ICustomer } from "./Customer";
import { ItemMedium, ItemNumber } from "./ItemNumber";
import { Payment, IPayment } from "./Payment";
import { PriceGroupCode, IPriceGroupCode } from "./PriceGroupCode";
import { SalesOrder } from "./SalesOrder";
import { v4 as uuidv4 } from "uuid";
import { CustomerUser, ICustomerUser } from "./CustomerUser";
import { IKeyValue } from "../../core/KeyValue";

export enum CartStatus {
    Open,
    Submitted,
    Closed,
    Fulfilled
}

export class Cart implements ICart {
    constructor(cart?: ICart, preCalculatedTotalAmount: number = null) {
        this.cartId = cart?.cartId ?? uuidv4();
        this.status = cart?.status ?? CartStatus.Open;
        this.licenseTermsAccepted = cart?.licenseTermsAccepted;
        if (cart?.institutionCustomer)
            this.institutionCustomer = cart.institutionCustomer instanceof Customer ? cart.institutionCustomer : new Customer(cart.institutionCustomer);
        this.orderNumber = cart?.orderNumber;
        this.orderDate = cart?.orderDate;
        if (cart?.pricing)
            this.pricing = cart.pricing instanceof PriceGroupCode ? cart.pricing : new PriceGroupCode(cart.pricing);
        this.culture = cart?.culture;
        this.taxSchedule = cart?.taxSchedule;
        this.manualDiscountAmount = cart?.manualDiscountAmount;
        this.freightAmount = cart?.freightAmount ?? 0;
        this.freightTaxAmount = cart?.freightTaxAmount ?? 0;
        if (cart?.items)
            this.items = cart.items.map(item => new CartItem(this, item));
        this.billingSameAsShipping = cart?.billingSameAsShipping;
        if (cart?.billingCustomer)
            this.billingCustomer = cart.billingCustomer instanceof Customer ? cart.billingCustomer : new Customer(cart.billingCustomer);
        if (cart?.payments)
            this.payments = cart.payments.map(payment => payment instanceof Payment ? payment : new Payment(payment));
        if (cart?.shippingCustomer)
            this.shippingCustomer = cart.shippingCustomer instanceof Customer ? cart.shippingCustomer : new Customer(cart.shippingCustomer);
        this.manualComment = cart?.manualComment;
        this.fulfillmentComment = cart?.fulfillmentComment;
        this.paymentProviderName = cart?.paymentProviderName;
        this.paymentProviderErrorMessage = cart?.paymentProviderErrorMessage;
        if (cart?.fulfillmentProvider)
            this.fulfillmentProvider = cart.fulfillmentProvider instanceof StringDictionary ? cart.fulfillmentProvider : new StringDictionary(cart.fulfillmentProvider);
        if (cart?.user)
            this.user = new CustomerUser(cart?.user);
        if (preCalculatedTotalAmount != null)
            this._preCalculatedTotalAmount = preCalculatedTotalAmount;
    }

    cartId: string;
    status: CartStatus;
    licenseTermsAccepted: boolean;
    institutionCustomer: Customer;
    orderNumber: string;
    orderDate: string;
    pricing: PriceGroupCode;
    culture: string;
    taxSchedule: string;
    manualDiscountAmount: number;
    freightAmount: number;
    freightTaxAmount: number;
    items: CartItem[] = [];
    billingSameAsShipping: boolean;
    billingCustomer?: Customer;
    payments: Payment[] = [];
    shippingCustomer: Customer;
    manualComment: string;
    fulfillmentComment: string;
    paymentProviderName: IKeyValue;
    paymentProviderErrorMessage: string;
    fulfillmentProvider: StringDictionary = new StringDictionary();
    user?: CustomerUser;
    _preCalculatedTotalAmount: number;

    toJSON(): object {
        return {
            cartId: this.cartId,
            status: this.status,
            licenseTermsAccepted: this.licenseTermsAccepted,
            institutionCustomer: this.institutionCustomer,
            orderNumber: this.orderNumber,
            orderDate: this.orderDate,
            //pricing - We don't want to include the pricing property, that be provided at runtime by other objects attaching it
            culture: this.culture,
            taxSchedule: this.taxSchedule,
            manualDiscountAmount: this.manualDiscountAmount,
            freightAmount: this.freightAmount > 0 ? this.freightAmount : undefined,
            freightTaxAmount: this.freightTaxAmount > 0 ? this.freightTaxAmount : undefined,
            items: this.items,
            billingSameAsShipping: this.billingSameAsShipping,
            billingCustomer: this.billingCustomer,
            payments: this.payments,
            shippingCustomer: this.shippingCustomer,
            manualComment: this.manualComment,
            fulfillmentComment: this.fulfillmentComment,
            paymentProviderName: this.paymentProviderName,
            paymentProviderErrorMessage: this.paymentProviderErrorMessage,
            fulfillmentProvider: this.fulfillmentProvider,
            currencyCode: this.currencyCode,
            priceCode: this.priceCode,
            user: this.user
        };
    }

    //Begin Calculated properties

    private _priceCode: string;
    get priceCode(): string {
        return this.pricing ? this.pricing.priceCode : this._priceCode;
    }
    set priceCode(value: string) {
        this._priceCode = value;
    }

    private _currencyCode: string;
    get currencyCode(): string {
        return this.pricing ? this.pricing.priceList.currencyCode : this._currencyCode;
    }
    set currencyCode(value: string) {
        this._currencyCode = value;
    }

    get paymentTerms(): string {
        return this.paymentProviderName.value;
    }

    get shippingTerms(): string {
        return this.fulfillmentProvider.aggregate("", (current, pair) => current + (current != "" ? "," : "") + pair.Value);
    }

    get totalItemAmount(): number {
        return Arrays.sum(this.items, (item) => (item as CartItem).baseAmount);
    }

    get totalDiscountedItemAmount(): number {
        return Arrays.sum(this.items, (item) => (item as CartItem).discountedBaseAmount);
    }

    get totalItemUnitDiscountAmount(): number {
        return Arrays.sum(this.items, (item) => (item as CartItem).unitDiscountAmount);
    }

    get totalItemDiscountAmount(): number {
        return Arrays.sum(this.items, (item) => (item as CartItem).discountAmount);
    }

    get totalDiscountAmount(): number {
        return this.totalItemDiscountAmount + (this.manualDiscountAmount ?? 0);
    }

    get totalTaxAmount(): number {
        return Arrays.sum(this.items, (item) => (item as CartItem).taxAmount ?? 0) + (this.freightTaxAmount ?? 0);
    }

    get totalAmount(): number {
        if (this._preCalculatedTotalAmount != null)
            return this._preCalculatedTotalAmount;
        return this.totalItemAmount - this.totalDiscountAmount + (this.freightAmount ?? 0) + this.totalTaxAmount;
    }

    get totalPaymentAmount(): number {
        return Arrays.sum(this.payments, (item) => (item as Payment).amount);
    }

    get balanceDueAmount(): number {
        return this.totalAmount - this.totalPaymentAmount;
    }

    get hasIndividualLicenseBasedItems(): boolean {
        return this.items.find(item => item.isIndividualLicenseBasedItem) != null;
    }

    get hasGroupLicenseBasedItems(): boolean {
        return this.items.find(item => item.isGroupLicenseBasedItem) != null;
    }

    get hasShippableItems() {
        return this.items.find(item => item.productNumber.medium === ItemMedium.Box);
    }

    //End Calculated properties

    //Begin Manipulation Methods

    findItem(itemId: string, throwNotFound: boolean = true): CartItem  {
        const foundItem = this.items.find(i => i.itemId === itemId);
        if (foundItem == null && throwNotFound)
            throw new Error(`"${itemId}" does not exist in Items.`);
        return foundItem;
    }

    addItem(productNumber: string, unitCode: string, description: string = null, quantity: number = 1, properties: StringDictionary = null) {
        let matchItem: CartItem = null;
        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            let match = item.productNumber.toString() == productNumber && item.unitCode == unitCode && (properties?.length ?? 0) == (item.properties?.length ?? 0);
            if (match && properties != null && item.properties != null)
                if (item.properties.find(property => property.Value != properties[property.Key]))
                    match = false;
            if (match) {
                matchItem = item;
                break;
            }
        }

        if (matchItem != null)
            matchItem.quantity += quantity;
        else {
            const ci = new CartItem(this);
            ci.itemId = uuidv4();
            ci.productNumber = new ItemNumber(productNumber);
            ci.unitCode = unitCode;
            ci.description = description;
            ci.quantity = quantity;
            ci.properties = properties;
            this.items.push(ci);
        }
    }

    removeItem(item: CartItem) {
        if (item == null)
            throw new Error("item to remove not specified");
        let index = this.items.findIndex(testItem => testItem.itemId === item.itemId);
        if (index > -1)
            this.items.splice(index, 1);
    }


    findPayment(paymentId: string, throwNotFound: boolean = true): Payment {
        const foundItem = this.payments.find(i => i.paymentId === paymentId);
        if (foundItem == null && throwNotFound)
            throw new Error(`"${paymentId}" does not exist in Payments.`);
        return foundItem;
    }

    removePayment(payment: Payment) {
        if (payment == null)
            throw new Error("missing payment");
        let index = this.payments.findIndex(testPayment => testPayment.paymentId === payment.paymentId);
        if (index > -1)
            this.payments.splice(index, 1);
    }

    toSalesOrder(): SalesOrder {
        throw new Error("Not implemented yet");
    }

    /*
    toSalesOrder(): SalesOrder {
        let so = new SalesOrder();
        so.origin = TransactionOrigin.Other;
        so.orderDate = this.orderDate;
        so.orderNumber = this.orderNumber;
        so.shippingCustomer = this.shippingCustomer.mergeCustomer(this.institutionCustomer);
        so.billingSameAsShipping = this.billingSameAsShipping;
        so.culture = this.culture;
        so.currencyCode = this.currencyCode ?? Currency.defaultCurrencyCode;
        so.freightAmount = this.freightAmount;
        so.manualDiscountAmount = this.manualDiscountAmount;
        so.paymentTerms = this.paymentProviderName.Value;
        so.priceCode = this.priceCode;
        so.shippingMethods = this.deliveryProviders.values;
        so.taxSchedule = so.taxSchedule;
        so.freightTaxAmount = so.freightTaxAmount;
        so.payments = so.payments;

        let comments = "";
        if (!string.IsNullOrWhiteSpace(FulfillmentComment))
            comments += this.fulfillmentComment + "\r\n";
        if (!string.IsNullOrWhiteSpace(ManualComment))
            comments += this.manualComment + "\r\n";
        so.comments = comments.trim();

        if (!this.billingSameAsShipping)
            so.billingCustomer = this.billingCustomer.mergeCustomer(this.institutionCustomer);

        this.items.forEach(item => {
            let si = new SalesItem();
            si.productNumber = item.productNumber;
            si.unitCode = item.unitCode;
            si.description = item.title;
            si.quantity = item.quantity;
            si.unitPrice = item.unitPrice;
            si.manualDiscountAmount = item.manualDiscountAmount;
            si.taxAmount = item.taxAmount;
            si.properties = item.properties;
            so.addItem(si);
        });

        return so;
    }
    */

    //End Manipulation Methods
}

export interface ICart {
    cartId: string;
    status: CartStatus;
    licenseTermsAccepted: boolean;
    institutionCustomer: ICustomer;
    orderNumber: string;
    orderDate: string;
    pricing: IPriceGroupCode;
    culture: string;
    taxSchedule: string;
    manualDiscountAmount: number;
    freightAmount: number;
    freightTaxAmount: number;
    items: ICartItem[];
    billingSameAsShipping: boolean;
    billingCustomer?: ICustomer;
    payments: IPayment[];
    shippingCustomer: ICustomer;
    manualComment: string;
    fulfillmentComment: string;
    paymentProviderName: IKeyValue;
    paymentProviderErrorMessage: string;
    fulfillmentProvider: IStringDictionary;
    currencyCode: string;
    priceCode: string;
    user?: ICustomerUser;
}